import { z } from 'zod';

import { looseBoolean, looseString } from '@/shared/lib/looseSchema';

/**
 * `GET /director/unpaid/unsigned/documents` — everything across the director's
 * whole portfolio that still needs a signature or a payment.
 *
 * The legacy portal decided what a row *was* by comparing its display name
 * against the strings 'ASIC Invoice' and 'Annual Review'. That made a backend
 * copy change silently alter which actions appeared. Here identity comes from
 * `documentType` and the shape of the row, and display names are derived last —
 * never matched on.
 */

/** Payment states, as the API spells them. `requires_payment_method` is
 *  Stripe's wording for a charge that failed and needs a different card. */
export const PAY_STATUS = {
  paid: 'paid',
  unpaid: 'unpaid',
  processing: 'processing',
  failed: 'requires_payment_method',
} as const;

export const SIGN_STATUS = {
  signed: 'signed',
  unsigned: 'unsigned',
} as const;

/** `dividend` is absent from the legacy `DOCUMENT_TYPE` constant even though
 *  the code branches on it — it was only ever a bare string literal. */
export const DOCUMENT_TYPE = {
  form: 'form',
  invoice: 'invoice',
  dividend: 'dividend',
} as const;

export type DocumentType = (typeof DOCUMENT_TYPE)[keyof typeof DOCUMENT_TYPE];

export const ISSUED_BY_ASIC = 'Asic';

/** An invoice attached to an outstanding row. Distinct from the standalone
 *  invoice record in the `documents` feature: this one is a line on a row. */
export const attachedInvoiceSchema = z.object({
  id: z.string().default(''),
  amount: z.coerce.number().default(0),
  dateIssued: z.string().default(''),
  fileURL: looseString,
  issuedBy: z.string().default(''),
  /** True when the paying entity is a self-managed super fund. The ATO forbids
   *  settling SMSF liabilities by credit card, so an agent who sets
   *  `disableSmsfCC` has card payment closed off on these invoices. */
  superFund: looseBoolean,
});

export type AttachedInvoice = z.infer<typeof attachedInvoiceSchema>;

/** The solvency resolution that accompanies an annual review. Only its id and
 *  file are ever read, but the id matters: it is the document that actually
 *  gets signed, not the row's own id. */
export const solvencySchema = z.object({
  id: z.string().default(''),
  fileURL: looseString,
});

export const outstandingItemSchema = z.object({
  id: z.string().default(''),
  asicCompanyId: z.string().default(''),
  asicCompanyName: z.string().default(''),
  /** Form number — '484', '362'. Empty for anything that is not a form. */
  type: z.string().default(''),
  /** The human-facing reference a director quotes to their agent. */
  changeInternalId: z.string().default(''),
  documentType: z.string().default(''),
  name: z.string().default(''),
  dueDate: z.string().nullish(),
  payStatus: z.string().default(''),
  signStatus: z.string().default(''),
  /** ASIC's own processing state, free text. Display only. */
  asicStatus: z.string().nullish(),
  fileURL: looseString,
  paymentReceiptURL: looseString,
  /** Empty when the row is a standalone invoice rather than an annual review. */
  annualReviewId: z.string().default(''),
  asicAmount: z.coerce.number().default(0),
  agentAmount: z.coerce.number().default(0),
  agentInvoiceStatus: z.string().default(''),
  asicInvoiceStatus: z.string().default(''),
  /** True when *this* director has signed but others still have to. */
  hasSigned: looseBoolean,
  invoices: z.array(attachedInvoiceSchema).default([]),
  solvency: solvencySchema.nullish(),
});

export type OutstandingItem = z.infer<typeof outstandingItemSchema>;

/**
 * The endpoint answers with a bare array, or an envelope, depending on which
 * branch of the backend serves it. Both are accepted and normalised by the
 * endpoint rather than being handled at every call site.
 */
export const outstandingResponseSchema = z.union([
  z.array(outstandingItemSchema),
  z.object({ data: z.array(outstandingItemSchema).default([]) }),
]);
